import React from 'react'
import { Box, Button, Card, CardContent, FormControl, FormControlLabel, Grid, Radio, Stack, Typography, IconButton, SvgIcon } from '@mui/material'
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { addCandidateResume } from 'api/users';
import { useSnackbar } from 'components/snackbar';

const CorrectTick = `${process.env.REACT_APP_HOMEPAGE}assets/correct-tick.png`;
const loginLogo = `${process.env.REACT_APP_HOMEPAGE}assets/TJ-Logo-LightBG-withTagline 1.svg`;
const CREATE_RESUME_STYLE = 'talent-junction-style';
const UPLOAD_EXISTING_RESUME = 'using-existing-resume';

/**
 * Component to onboard a user by allowing them to create or upload a resume.
 * Provides options to either create a new resume using the Talent Junction style
 * or upload an existing resume file. Handles file selection, validation, and submission.
 * Displays success or error messages based on the outcome of resume uploads.
 * 
 * @param {function} onClose - Callback to execute when the onboarding process completes.
 * 
 * @returns {JSX.Element} - The rendered component containing options for resume onboarding.
 */

const ResumeOnboarded = ({ onClose, triggerFilePicker }) => {
    const [selectedOption, setSelectedOption] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [isFileSelected, setIsFileSelected] = useState(false);
    const [disableContinueButton, setDisableContinueButton] = useState(false);
    const fileInputRef = useRef(null);
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();

    const handleOptionClick = (option) => {
        if (isSubmitting) return;
        setSelectedOption(option);
    };

    useEffect(() => {
        if (triggerFilePicker && fileInputRef.current) {
            fileInputRef.current.click();
        }
    }, [triggerFilePicker]);

    /**
     * Handles the file change event for the file input element.
     * Checks if the selected file is valid (not empty, less than 2MB, and of allowed type).
     * If the file is valid, it sets the selected file in component state and submits the file
     * for upload. If the upload fails, it logs an error to the console and displays an error
     * message to the user.
     * @param {Event} event - The file change event.
     * @returns {boolean} - Whether the file change was handled successfully.
     */
    const handleFileChange = (event) => {
        const file = event.target.files[0];

        if (!file) {
            enqueueSnackbar("No file selected. Please choose a file to upload", { variant: "error" });
            fileInputRef.current.click();
            return;
        }

        // Check file size first
        if (file.size > 2 * 1024 * 1024) {
            enqueueSnackbar("File size should be less than 2MB", { variant: "error" });
            fileInputRef.current.click();
            return;
        }
        const allowedTypes = [
            "application/pdf",
            "application/msword",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        ];

        if (!allowedTypes.includes(file.type)) {
            enqueueSnackbar("Invalid file type. Only PDF, DOC, and DOCX files are allowed", { variant: "error" });
            return;
        }

        setSelectedFile(file);
        setIsFileSelected(true);
        handleFileSubmit(file);
    };

    /**
     * Handles the form submission based on the selected option.
     * If "create" is selected, it closes the dialog and navigates to the root route.
     * If "existing" is selected, it simulates a click on the file input element to
     * open the file selector dialog.
     * If a file is selected and the user has selected "upload", it submits the
     * selected file for upload.
     */
    const handleSubmit = () => {
        if (selectedOption === CREATE_RESUME_STYLE) {
            onClose({ choice: CREATE_RESUME_STYLE, resumeUrl: null });
            navigate('/');
        } else if (selectedOption === UPLOAD_EXISTING_RESUME) {
            fileInputRef.current.click();
        } else if (selectedFile && isFileSelected) {
            handleFileSubmit(file);
        }
    };

    /**
     * Handles the file submission by uploading the selected file to the server.
     * If the upload is successful, it updates the local resume data and displays a success message to the user.
     * If the upload fails, it displays an error message to the user.
     * Finally, it sets the isFileSelected state to false to reset the file input element.
     * @param {File} selectedFile - The file selected by the user to upload.
     */
    const handleFileSubmit = async (selectedFile) => {
        try {
            const formData = new FormData();
            formData.append("file", selectedFile);
            const response = await addCandidateResume(formData);
            if (response?.success) {
                enqueueSnackbar("Resume uploaded successfully", { variant: "success" });
                const resumeUrl = `${process.env.REACT_APP_END_POINT}${response?.data?.resumeUrl}`;
                onClose({ choice: UPLOAD_EXISTING_RESUME, resumeUrl: resumeUrl });
            } else {
                enqueueSnackbar("Failed to upload resume. Please try again.", { variant: "error" });
            }
        } catch (error) {
            console.error("Upload Error:", error);
            enqueueSnackbar("Failed to upload resume. Please try again.", { variant: "error" });
        } finally {
            setIsSubmitting(false);
            setIsFileSelected(false);
            setSelectedFile(null);
        }
    }

    return (
        <Card variant="outlined" className="resume-onboarded-card-container" sx={{ padding: { xs: 0, md: '24px 32px' } }}>
            <CardContent sx={{ padding: { xs: '16px', md: '0!important' } }}>
                <Box mb={3}>
                    <Stack direction={'row'} justifyContent={'flex-start'} spacing={2}>
                        <Box
                            className="logo"
                            component="img"
                            alt="Talent Junction"
                            src={loginLogo}
                        />
                    </Stack>
                </Box>
                <Box mb={2.5}>
                    <Stack spacing={1}>
                        <Typography className="title">Let’s get you onboarded...</Typography>
                        <Typography className="info-text" sx={{ textAlign: 'left!important' }}>Please select an option below to help us create your profile and match you with the most suitable job opportunities</Typography>
                    </Stack>
                </Box>
                <Box>
                    <Grid container spacing={2}>
                        {/* Create a resume option */}
                        <Grid item xs={12} md={6}>
                            <Box className={`resume-onboarded-card ${selectedOption === CREATE_RESUME_STYLE ? "selected" : ""}`} onClick={() => handleOptionClick(CREATE_RESUME_STYLE)}>
                                <Box className="card-top">
                                    <Box className="recommended-label"><Typography>RECOMMENDED</Typography></Box>
                                    <FormControl sx={{ marginBottom: '12px' }} fullWidth>
                                        <FormControlLabel
                                            value={CREATE_RESUME_STYLE}
                                            control={<Radio checked={selectedOption === CREATE_RESUME_STYLE} />}
                                            label="Create a resume"
                                            labelPlacement="start"
                                            componentsProps={{
                                                typography: { sx: { fontSize: '14px !important' } }
                                            }}
                                        />
                                    </FormControl>
                                    <Stack className="list-item" spacing={1}>
                                        <Stack direction="row" spacing={1}>
                                            <Box
                                                component="img"
                                                alt="Talent Junction"
                                                src={CorrectTick}
                                                sx={{ width: "12px", height: "12px", marginTop: "3px!important" }}
                                            />
                                            <Typography>World class resume in minutes</Typography>
                                        </Stack>
                                        <Stack direction="row" spacing={1}>
                                            <Box
                                                component="img"
                                                alt="Talent Junction"
                                                src={CorrectTick}
                                                sx={{ width: "12px", height: "12px", marginTop: "3px!important" }}
                                            />
                                            <Typography>Add multiple customised versions</Typography>
                                        </Stack>
                                        <Stack direction="row" spacing={1}>
                                            <Box
                                                component="img"
                                                alt="Talent Junction"
                                                src={CorrectTick}
                                                sx={{ width: "12px", height: "12px", marginTop: "3px!important" }}
                                            />
                                            <Typography>Best job matching</Typography>
                                        </Stack>
                                    </Stack>
                                </Box>
                            </Box>
                        </Grid>

                        {/* Already have resume option */}
                        <Grid item xs={12} md={6}>
                            <Box className={`resume-onboarded-card ${selectedOption === UPLOAD_EXISTING_RESUME ? "selected" : ""}`} onClick={() => handleOptionClick(UPLOAD_EXISTING_RESUME)}>
                                <Box className="card-top" sx={{ height: { xs: 'auto', md: '180px!important' } }}>
                                    <FormControl sx={{ marginBottom: "12px" }} fullWidth>
                                        <FormControlLabel
                                            value={UPLOAD_EXISTING_RESUME}
                                            control={<Radio checked={selectedOption === UPLOAD_EXISTING_RESUME} />}
                                            label="Upload your resume"
                                            labelPlacement="start"
                                            componentsProps={{
                                                typography: { sx: { fontSize: '14px !important' } }
                                            }}
                                        />
                                    </FormControl>
                                    <Stack className="list-item" spacing={1}>
                                        <Stack direction="row" spacing={1}>
                                            <Box
                                                component="img"
                                                alt="Talent Junction"
                                                src={CorrectTick}
                                                sx={{ width: "12px", height: "12px", marginTop: "3px!important" }}
                                            />
                                            <Typography>Create world class TJ format resume later</Typography>
                                        </Stack>
                                    </Stack>
                                    <Typography
                                        sx={{
                                            color: "red",
                                            fontSize: "9px",
                                            marginTop: "55px"
                                        }}
                                    >
                                        PDF format highly recommended
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
                <input type="file"
                    accept=".pdf, .doc, .docx, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                />
                {/* Continue Button */}
                <Box sx={{ marginTop: { xs: '24px', md: '32px' } }}>
                    <Button fullWidth className="continue-btn" variant="contained" size="medium" onClick={handleSubmit} disabled={!selectedOption || isSubmitting}>
                        Continue
                    </Button>
                </Box>
            </CardContent>
        </Card >
    )
}

export default ResumeOnboarded